import type { MarkToMarketItem } from '@/types';

/**
 * Shared formatter for prices coming off the MarkToMarket audit trail.
 *
 * Backend stores prices as DecimalField(decimal_places=4) and ships them as
 * strings; this preserves their up-to-4-decimal precision in the UI while
 * always showing at least 2 fraction digits for visual stability. Used by
 * MtmTable's "Last Marked" column and MtmDetailsDrawer's mark list so the
 * same record renders identically in both places.
 */
export function formatMarkPrice(value: string | number | null | undefined): string {
  if (value == null) return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Δ vs the previous (older) mark, or null if there is no older mark.
 * `marks` is newest-first (matching backend order: -marked_at, -id), so the
 * previous mark for `marks[i]` is `marks[i + 1]`. Returns null for the oldest
 * entry (no predecessor) or when either price fails to parse to a finite Number.
 */
export function priceDelta(marks: MarkToMarketItem[], index: number): number | null {
  const prev = marks[index + 1];
  if (!prev) return null;
  const cur = Number(marks[index].price);
  const old = Number(prev.price);
  if (!Number.isFinite(cur) || !Number.isFinite(old)) return null;
  return cur - old;
}
